import { Response } from '../lib/types/response';
import { PortfolioData, Asset } from '../types';
import { CacheData, CacheDataHelper } from '../lib/cache';

export class PortfolioService {
  private static instance: PortfolioService;

  private constructor() {}

  static getInstance(): PortfolioService {
    if (!PortfolioService.instance) {
      PortfolioService.instance = new PortfolioService();
    }
    return PortfolioService.instance;
  }

  /**
   * Load portfolio data from localStorage
   */
  loadFromLocalStorage(): Response<PortfolioData> {
    try {
      const storedData = localStorage.getItem('cryptoPortfolio');
      if (storedData) {
        const portfolioData = JSON.parse(storedData);
        return this.validatePortfolioData(portfolioData);
      }
      return { buckets: {}, assets: {} };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to parse localStorage data';
      return new Error(`Failed to load portfolio data from localStorage: ${message}`);
    }
  }

  /**
   * Save portfolio data to localStorage
   */
  saveToLocalStorage(portfolioData: PortfolioData): Response<void> {
    try {
      const validationResult = this.validatePortfolioData(portfolioData);
      if (validationResult instanceof Error) {
        return validationResult;
      }

      localStorage.setItem('cryptoPortfolio', JSON.stringify(portfolioData));
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to save portfolio data to localStorage: ${message}`);
    }
  }

  /**
   * Clear portfolio data from localStorage
   */
  clearLocalStorage(): Response<void> {
    try {
      localStorage.removeItem('cryptoPortfolio');
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to clear localStorage: ${message}`);
    }
  }

  /**
   * Check if portfolio data contains any assets or buckets
   */
  hasPortfolioData(portfolioData: PortfolioData): boolean {
    return Object.keys(portfolioData.buckets || {}).length > 0 || 
           Object.keys(portfolioData.assets || {}).length > 0;
  }

  /**
   * Validate portfolio data structure
   */
  validatePortfolioData(data: object): Response<PortfolioData> {
    try {
      if (!data || typeof data !== 'object') {
        return new Error('Portfolio data must be an object');
      }

      const dataRecord = data as Record<string, Record<string, object> | undefined>;
      const portfolioData: PortfolioData = {
        buckets: (dataRecord.buckets as Record<string, string[]>) || {},
        assets: (dataRecord.assets as Record<string, Asset>) || {}
      };

      // Validate buckets structure
      if (typeof portfolioData.buckets !== 'object') {
        return new Error('Buckets must be an object');
      }

      // Validate assets structure
      if (typeof portfolioData.assets !== 'object') {
        return new Error('Assets must be an object');
      }

      // Validate each bucket contains an array
      for (const [bucketName, assetIds] of Object.entries(portfolioData.buckets)) {
        if (!Array.isArray(assetIds)) {
          return new Error(`Bucket '${bucketName}' must contain an array of asset IDs`);
        }
      }

      // Validate each asset has required properties
      for (const [assetId, asset] of Object.entries(portfolioData.assets)) {
        if (!asset || typeof asset !== 'object') {
          return new Error(`Asset '${assetId}' must be an object`);
        }

        const requiredFields = ['id', 'name', 'symbol', 'amount', 'bucket', 'type', 'platforms', 'lastUpdated'];
        for (const field of requiredFields) {
          if (!(field in asset)) {
            return new Error(`Asset '${assetId}' missing required field '${field}'`);
          }
        }

        if (typeof asset.amount !== 'number' || asset.amount < 0) {
          return new Error(`Asset '${assetId}' amount must be a non-negative number`);
        }
      }

      return portfolioData;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown validation error';
      return new Error(`Portfolio data validation failed: ${message}`);
    }
  }

  /**
   * Load cache data from localStorage
   */
  loadCacheFromLocalStorage(): Response<CacheData> {
    try {
      const storedData = localStorage.getItem('cryptoCache');
      if (storedData) {
        const cacheData = JSON.parse(storedData);
        return this.validateCacheData(cacheData);
      }
      return {};
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to parse cache localStorage data';
      return new Error(`Failed to load cache data from localStorage: ${message}`);
    }
  }

  /**
   * Save cache data to localStorage
   */
  saveCacheToLocalStorage(cacheData: CacheData): Response<void> {
    try {
      const validationResult = this.validateCacheData(cacheData);
      if (validationResult instanceof Error) {
        return validationResult;
      }

      localStorage.setItem('cryptoCache', JSON.stringify(cacheData));
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to save cache data to localStorage: ${message}`);
    }
  }

  /**
   * Clear cache data from localStorage
   */
  clearCacheLocalStorage(): Response<void> {
    try {
      localStorage.removeItem('cryptoCache');
      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to clear cache localStorage: ${message}`);
    }
  }

  /**
   * Merge cache data using latest-wins strategy
   */
  mergeCacheData(existingCache: CacheData, newCache: CacheData): CacheData {
    const merged: CacheData = { ...existingCache };
    
    for (const [cacheKey, newEntries] of Object.entries(newCache)) {
      const existingEntries = merged[cacheKey] || {};
      merged[cacheKey] = CacheDataHelper.mergeCacheData(existingEntries, newEntries);
    }
    
    return merged;
  }

  /**
   * Validate cache data structure
   */
  validateCacheData(data: object): Response<CacheData> {
    try {
      if (!data || typeof data !== 'object') {
        return new Error('Cache data must be an object');
      }

      const cacheData = data as CacheData;
      
      // Basic structure validation - cache data can be any shape as it's flexible
      for (const [cacheKey, entries] of Object.entries(cacheData)) {
        if (typeof entries !== 'object' || entries === null) {
          return new Error(`Cache entries for '${cacheKey}' must be an object`);
        }
        
        // Validate each cache entry has required fields
        for (const [entryKey, entry] of Object.entries(entries)) {
          if (typeof entry !== 'object' || entry === null) {
            return new Error(`Cache entry '${entryKey}' in '${cacheKey}' must be an object`);
          }
          
          const requiredFields = ['data', 'timestamp', 'expiresAt'];
          for (const field of requiredFields) {
            if (!(field in entry)) {
              return new Error(`Cache entry '${entryKey}' in '${cacheKey}' missing required field '${field}'`);
            }
          }
          
          if (typeof entry.timestamp !== 'number' || typeof entry.expiresAt !== 'number') {
            return new Error(`Cache entry '${entryKey}' in '${cacheKey}' timestamp and expiresAt must be numbers`);
          }
        }
      }

      return cacheData;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown validation error';
      return new Error(`Cache data validation failed: ${message}`);
    }
  }

  /**
   * Create backup of portfolio data (now includes cache data)
   */
  createBackup(portfolioData: PortfolioData, backupType: string = 'manual', autoDownload: boolean = true): Response<void> {
    try {
      // Include cache data in backup
      const cacheData = this.loadCacheFromLocalStorage();
      const backupData = {
        version: '1.0',
        backupType,
        createdAt: new Date().toISOString(),
        portfolioData,
        cacheData: cacheData instanceof Error ? {} : cacheData
      };

      const backupJson = JSON.stringify(backupData, null, 2);
      
      if (autoDownload) {
        const blob = new Blob([backupJson], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `crypto-portfolio-backup-${backupType}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      return undefined;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error occurred';
      return new Error(`Failed to create backup: ${message}`);
    }
  }
}

export const portfolioService = PortfolioService.getInstance();
